import { GuideDetailsContent } from '@fruitful/guidance';
import { atom, useAtom } from 'jotai';
import React, { useEffect, useRef } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    isNonMobileWeb,
    ResponsiveScreenModal,
    Sheet,
    useWindowDimensions,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { trackView } from '../../logger/Logger';
import type { Guide } from '../types/Guide';

export const isGuideDetailsModalOpenAtom = atom(false);
export const guideForDetailsModalAtom = atom<Guide | null>(null);

export const GuideDetailsModal = () => {
    const { width: screenWidth, height: screenHeight } = useRef(
        useWindowDimensions(),
    ).current;
    const [isOpen, setIsOpen] = useAtom(isGuideDetailsModalOpenAtom);
    const [guideForDetails, setGuideForDetails] = useAtom(
        guideForDetailsModalAtom,
    );

    const user = useAuthedUser();
    const { membership } = user ?? {};
    const { guide } = membership ?? {};

    const guideToDisplay = guideForDetails ?? guide;

    useEffect(() => {
        if (isOpen) {
            trackView(`GuideDetails/${guideToDisplay?.signUpId}`);
        }
    }, [isOpen]);

    useEffect(() => {
        return () => {
            setGuideForDetails(guide ?? null);
        };
    }, [guide]);

    const onOpenChange = (isOpen: boolean) => {
        !isOpen && setIsOpen(false);
    };

    if (!guideToDisplay) {
        return null;
    }

    return (
        <ResponsiveScreenModal
            isOpen={isOpen}
            hasHeader={false}
            withPadding={isNonMobileWeb}
            onOpenChange={onOpenChange}
        >
            <GuideDetailsContent
                guide={guideToDisplay}
                onOpenChange={onOpenChange}
                containerWidth={screenWidth}
                containerHeight={screenHeight}
            />
        </ResponsiveScreenModal>
    );
};

export const GuideDetailsSheet = ({
    isOpen,
    onOpenChange,
}: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
}) => {
    const [guideForDetails, setGuideForDetails] = useAtom(
        guideForDetailsModalAtom,
    );
    const { top } = useSafeAreaInsets();
    const { height: screenHeight, width: screenWidth } = useRef(
        useWindowDimensions(),
    ).current;
    const fixedContentHeight = (screenHeight - top) * 0.95;

    const handleClose = () => {
        onOpenChange(false);
    };

    useEffect(() => {
        if (isOpen && guideForDetails) {
            trackView(`GuideDetails/${guideForDetails.signUpId}`);
        }
    }, [isOpen]);

    useEffect(() => {
        return () => {
            setGuideForDetails(null);
        };
    }, []);

    if (!guideForDetails) {
        return null;
    }

    return (
        <Sheet
            isOpen={isOpen}
            onClose={handleClose}
            showCloseButton={false}
            withPadding={false}
            disableDrag={true}
            fixedContentHeight={fixedContentHeight}
            scrollContainerOverride={(children) => <>{children}</>}
        >
            <GuideDetailsContent
                key={`${guideForDetails.id}-${isOpen}`}
                guide={guideForDetails}
                onOpenChange={onOpenChange}
                isSheetLayout={true}
                containerHeight={fixedContentHeight}
                containerWidth={screenWidth}
            />
        </Sheet>
    );
};
